// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useRef, useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { ArrowRight, Lock, LockOpen, TriangleAlert } from "lucide-react"
import type {
  MealInstance,
  PlanChange,
  PlanMutationResult,
  PlanProposalRequest,
  PlanProposalResponse,
} from "@bitebalance/contracts"
import { ApiError, isDeadPlanPreview } from "@bitebalance/contracts/client"
import { useApiClient } from "@/api/useApiClient"
import {
  useI18n,
  formatEurFromCents,
  formatKcal,
  formatServings,
  formatShortDate,
  type I18nContextValue,
  type Locale,
} from "@/i18n"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { LoadingState, ErrorState } from "@/components/state"
import { humanizeRecipeId, reasonBadgeLabel, validationReasonMessage } from "./recipeDisplay"
import { CheckoutConflictNotice, GoalImpactLine, conflictAmounts } from "./BudgetImpactNotices"

export interface SwapProposalSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  baseRevision: number
  meal: MealInstance | null
  intent: PlanProposalRequest["intent"]
  onApplied: (result: PlanMutationResult) => void
  onStaleRevision: () => void
}

/**
 * Cost truth (ADR-055 acceptance 2): a side whose recipe has no price
 * estimate arrives as `costCents: 0` under COST_ESTIMATE_UNAVAILABLE — that
 * 0 is NOT a price and must never render as €0.00 (it would read as free).
 */
/**
 * Minutes truth (2026-10-07): the recipe's hands-on minutes from the wire, and
 * the total only when it differs. A proposal that predates the fields (or a
 * side without them) renders nothing — never a guess. Same words as the
 * mobile twin (`mobile/src/lib/swapCandidatesView.ts` `minutesLines`).
 */
function minutesLine(
  side: { activeMinutes?: number; totalMinutes?: number },
  t: I18nContextValue["t"]
): string | null {
  if (side.activeMinutes === undefined) return null
  const active = t("views.recipe.heroActive", { minutes: side.activeMinutes })
  return side.totalMinutes !== undefined && side.totalMinutes !== side.activeMinutes
    ? `${active} · ${t("views.recipe.heroTotal", { minutes: side.totalMinutes })}`
    : active
}

function costLabel(change: PlanChange, costCents: number, t: I18nContextValue["t"], locale: Locale): string {
  if (costCents === 0 && change.reasonCodes.includes("COST_ESTIMATE_UNAVAILABLE")) {
    return t("views.recipe.costUnavailable")
  }
  return formatEurFromCents(costCents, locale)
}

type Phase =
  | "loading"
  | "ready"
  | "applying"
  | "applied"
  | "error"
  | "stale"
  // ADR-028 Phase 2: honest non-error outcomes of offer-aware proposals.
  | "noCheaper" // 422 NO_CHEAPER_FOUND — nothing valid was cheaper this week
  | "offerExpired" // 409 OFFER_EXPIRED — the backing offer ended before apply
  // Budget Model v2 (owner D-2): 422 MAX_CHECKOUT_CONFLICT at apply — the
  // resulting checkout would exceed the hard maximum; no override exists.
  | "checkoutConflict"
  // Acceptance 3 (2026-09-04): 422 MAKE_ROOM_INFEASIBLE — the owner found no
  // safe change among the other meals that fits the anchored portion. An
  // honest, user-level outcome (never the structured details, never a retry
  // that would answer the same).
  | "makeRoomInfeasible"

/**
 * Shared validated-proposal diff sheet (T12): request a scoped proposal,
 * render the before/after + deltas, and only allow Approve when
 * `validation.status === "PASS"` (the contract's `confirmationToken` is
 * `null` whenever it's FAIL, so there is structurally nothing to apply).
 * Reused for swap/lock/unlock — all are scoped plan proposals per
 * api-contracts.md, never a silent direct mutation.
 */
export function SwapProposalSheet({
  open,
  onOpenChange,
  planId,
  baseRevision,
  meal,
  intent,
  onApplied,
  onStaleRevision,
}: SwapProposalSheetProps) {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const reduce = useReducedMotion()
  const [phase, setPhase] = useState<Phase>("loading")
  const [proposal, setProposal] = useState<PlanProposalResponse | null>(null)
  const [applyConflict, setApplyConflict] = useState<{
    overCents: number | null
    maxCents: number | null
  } | null>(null)
  const requestId = useRef(0)

  const requestProposal = useCallback(() => {
    if (!meal) return
    const id = ++requestId.current
    setPhase("loading")
    setProposal(null)
    setApplyConflict(null)
    api
      .createPlanProposal(planId, {
        baseRevision,
        scope: { mealIds: [meal.id] },
        intent,
      })
      .then(result => {
        if (requestId.current !== id) return
        setProposal(result)
        setPhase("ready")
      })
      .catch((err: unknown) => {
        if (requestId.current !== id) return
        if (err instanceof ApiError && err.envelope.code === "STALE_REVISION") {
          setPhase("stale")
          return
        }
        if (err instanceof ApiError && err.envelope.code === "NO_CHEAPER_FOUND") {
          // Honest empty result of a cheaper-swap request — not an error.
          setPhase("noCheaper")
          return
        }
        if (err instanceof ApiError && err.envelope.code === "MAKE_ROOM_INFEASIBLE") {
          setPhase("makeRoomInfeasible")
          return
        }
        setPhase("error")
      })
  }, [api, planId, baseRevision, meal, intent])

  useEffect(() => {
    if (open && meal) requestProposal()
    // requestProposal intentionally excluded: it's re-created per meal/intent
    // change already covered by the deps below, and including it would
    // re-fire this effect on every unrelated parent re-render.
  }, [open, meal, intent, requestProposal])

  async function handleApprove() {
    if (!proposal || !proposal.confirmationToken) return
    setPhase("applying")
    try {
      // Budget Model v2 (owner D-2): there is no over-maximum override flag —
      // the server refuses a checkout above the hard ceiling with 422.
      const result = await api.applyPlanProposal(planId, proposal.proposalId, {
        confirmationToken: proposal.confirmationToken,
        baseRevision: proposal.baseRevision,
      })
      setPhase("applied")
      onApplied(result)
      onOpenChange(false)
    } catch (err) {
      if (err instanceof ApiError && err.envelope.code === "STALE_REVISION") {
        setPhase("stale")
        return
      }
      if (isDeadPlanPreview(err)) {
        // F14: a NEWER preview superseded this one (or it was consumed / expired) — the held token can never be
        // applied, so drop it and treat it like a stale plan: the sheet closes, the page reloads, a fresh one is
        // requested. Never "Retry" the same token.
        setProposal(null)
        setPhase("stale")
        return
      }
      if (err instanceof ApiError && err.envelope.code === "MAX_CHECKOUT_CONFLICT") {
        const d = (err.envelope.details ?? {}) as {
          maxCheckoutCents?: number
          checkoutDeltaCents?: number
        }
        setApplyConflict({
          overCents: d.checkoutDeltaCents ?? null,
          maxCents: d.maxCheckoutCents ?? null,
        })
        setPhase("checkoutConflict")
        return
      }
      if (err instanceof ApiError && err.envelope.code === "OFFER_EXPIRED") {
        // The offer backing this proposal ended between preview and apply
        // (ADR-028 rule 4: apply requires a still-valid offer). Tell the
        // user honestly and let them request a fresh proposal.
        setPhase("offerExpired")
        return
      }
      setPhase("error")
    }
  }

  function handleCancel() {
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto sm:max-w-full">
        <SheetHeader>
          <SheetTitle>{t("views.proposal.title")}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-4 px-4 pb-4">
          {phase === "loading" && <LoadingState label={t("views.proposal.loading")} />}

          {phase === "stale" && (
            <ErrorState message={t("views.plan.staleReloadBody")} onRetry={onStaleRevision} />
          )}

          {phase === "error" && (
            <ErrorState message={t("views.proposal.error")} onRetry={requestProposal} />
          )}

          {phase === "noCheaper" && (
            // Honest empty result, deliberately NOT the error styling: the
            // request worked — there just is nothing valid that's cheaper.
            <p
              role="status"
              className="rounded-bb-lg border border-line bg-cream p-4 text-sm text-ink"
            >
              {t("views.proposal.noCheaperFound")}
            </p>
          )}

          {phase === "makeRoomInfeasible" && (
            <p
              role="status"
              className="rounded-bb-lg border border-line bg-cream p-4 text-sm text-ink"
            >
              {t("views.proposal.makeRoomInfeasible")}
            </p>
          )}

          {phase === "offerExpired" && (
            <div
              role="alert"
              className="flex flex-col gap-3 rounded-bb-lg border border-gold/60 bg-gold/10 p-4"
            >
              <p className="text-sm text-ink">{t("views.proposal.offerExpired")}</p>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 self-start"
                onClick={requestProposal}
              >
                {t("views.proposal.tryAnother")}
              </Button>
            </div>
          )}

          {phase === "checkoutConflict" && (
            <CheckoutConflictNotice
              overCents={applyConflict?.overCents ?? null}
              maxCents={applyConflict?.maxCents ?? null}
            />
          )}

          {(phase === "ready" || phase === "applying") && proposal && (
            <>
              {proposal.validation.status === "FAIL" ? (
                <div
                  role="alert"
                  className="flex gap-3 rounded-bb-lg border border-coral/40 bg-coral/10 p-4"
                >
                  <TriangleAlert className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden="true" />
                  <div>
                    <p className="font-semibold text-ink">{t("views.proposal.failTitle")}</p>
                    <ul className="mt-1 flex flex-col gap-1 text-sm text-ink-2">
                      {proposal.validation.failures.map((f, i) => (
                        <li key={`${f.code}-${i}`}>{validationReasonMessage(f.code, t)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <p className="text-sm font-medium text-ink-2">{t("views.proposal.changesTitle")}</p>
              )}

              <ul className="flex flex-col gap-3">
                {proposal.changes.map((change, i) => (
                  <motion.li
                    key={`${change.mealId}-${i}`}
                    initial={reduce ? false : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: reduce ? 0 : i * 0.06 }}
                    className="flex flex-col gap-2 rounded-bb-lg border border-line bg-cream p-3 text-sm"
                  >
                    {/* Owner bug 2026-08-30: LOCK/UNLOCK rendered through the
                        before→after grid as meaningless dashes. They are a
                        SENTENCE, not a diff. */}
                    {change.type === "LOCK_MEAL" || change.type === "UNLOCK_MEAL" ? (
                      <p className="flex items-center gap-2.5 py-1 font-medium text-ink">
                        {change.type === "LOCK_MEAL" ? (
                          <Lock className="size-4 shrink-0 text-green" aria-hidden="true" />
                        ) : (
                          <LockOpen className="size-4 shrink-0 text-green" aria-hidden="true" />
                        )}
                        {t(
                          change.type === "LOCK_MEAL"
                            ? "views.proposal.lockChange"
                            : "views.proposal.unlockChange",
                          { title: mealDisplayTitle(meal, change.mealId) }
                        )}
                      </p>
                    ) : (
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold tracking-wide text-ink-2 uppercase">
                          {t("views.proposal.beforeLabel")}
                        </p>
                        {change.before ? (
                          <>
                            <p className="truncate font-medium text-ink">
                              {humanizeRecipeId(change.before.recipeId)}
                            </p>
                            <p className="text-ink-2">
                              {formatKcal(change.before.kcal, locale)} ·{" "}
                              {costLabel(change, change.before.costCents, t, locale)}
                            </p>
                            {minutesLine(change.before, t) && (
                              <p data-testid="change-minutes-before" className="text-ink-2">
                                {minutesLine(change.before, t)}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-ink-2">—</p>
                        )}
                      </div>
                      <motion.span
                        initial={reduce ? false : { x: -4, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.15 }}
                      >
                        <ArrowRight className="size-4 shrink-0 text-green" aria-hidden="true" />
                      </motion.span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold tracking-wide text-ink-2 uppercase">
                          {t("views.proposal.afterLabel")}
                        </p>
                        {change.after ? (
                          <>
                            <p className="truncate font-medium text-ink">
                              {humanizeRecipeId(change.after.recipeId)}
                            </p>
                            <p className="text-ink-2">
                              {formatKcal(change.after.kcal, locale)} ·{" "}
                              {costLabel(change, change.after.costCents, t, locale)}
                              {change.before && (
                                <span className="ml-1 font-semibold text-ink">
                                  ({signed(change.after.kcal - change.before.kcal)} kcal)
                                </span>
                              )}
                            </p>
                            {minutesLine(change.after, t) && (
                              <p data-testid="change-minutes-after" className="text-ink-2">
                                {minutesLine(change.after, t)}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-ink-2">—</p>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Portion truth (acceptance 3): a change that scales a
                        meal's serving (ADAPT_MEAL of the same recipe, or a
                        make_room step-down) is invisible in the recipe → recipe
                        grid above — the serving IS the change, so it is named
                        with the same "Portions: 1 → 1.5" line the recipe page
                        uses. Rendered only when both sides carry a serving
                        that differs; never computed on the device. */}
                    {change.before?.servings !== undefined &&
                      change.after?.servings !== undefined &&
                      change.before.servings !== change.after.servings && (
                        <p data-testid="change-servings" className="text-ink">
                          {t("views.recipe.recalcTitle", {
                            from: formatServings(change.before.servings, locale),
                            to: formatServings(change.after.servings, locale),
                          })}
                        </p>
                      )}

                    {/* ADR-028 Phase 2: offer-backed change — the localized
                        OFFER_MATCH badge plus one verbatim provenance line
                        per snapshotted offer. The before→after cost diff
                        above is untouched; the offer price is a separate
                        provenance figure, never blended per-serving. */}
                    {change.reasonCodes.includes("OFFER_MATCH") && (
                      <span className="inline-flex w-fit items-center rounded-bb-full border border-green/40 bg-green/10 px-2 py-0.5 text-xs font-medium text-green">
                        {reasonBadgeLabel("OFFER_MATCH", t)}
                      </span>
                    )}
                    {(change.offers ?? []).map(offer => (
                      <p key={offer.offerId} className="text-xs text-ink-2">
                        {t("views.proposal.offerProvenance", {
                          retailer: offer.retailer,
                          price: formatEurFromCents(offer.offerPriceCents, locale),
                          date: formatShortDate(offer.validTo, locale),
                          source: offer.source,
                        })}
                      </p>
                    ))}
                  </motion.li>
                ))}
              </ul>

              {proposal.impact.estimatedTimeDeltaMinutes === 0 &&
              Math.round(proposal.impact.shoppingDelta * 100) === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t("views.proposal.noImpactHint")}
                </p>
              ) : (
                <dl className="grid grid-cols-2 gap-2">
                  {proposal.impact.estimatedTimeDeltaMinutes !== 0 && (
                    <div className="rounded-bb-lg border border-line bg-paper p-3">
                      <dt className="text-[11px] font-semibold tracking-wide text-ink-2 uppercase">
                        {t("views.proposal.timeDeltaLabel")}
                      </dt>
                      <dd className="text-base font-semibold text-ink">
                        {signed(proposal.impact.estimatedTimeDeltaMinutes)} min
                      </dd>
                      <dd className="text-[11px] text-muted-foreground">
                        {t("views.proposal.timeDeltaHint")}
                      </dd>
                    </div>
                  )}
                  {Math.round(proposal.impact.shoppingDelta * 100) !== 0 && (
                    <div className="rounded-bb-lg border border-line bg-paper p-3">
                      <dt className="text-[11px] font-semibold tracking-wide text-ink-2 uppercase">
                        {t("views.proposal.shoppingDeltaLabel")}
                      </dt>
                      <dd className="text-base font-semibold text-ink">
                        {formatSignedEur(proposal.impact.shoppingDelta, locale)}
                      </dd>
                      <dd className="text-[11px] text-muted-foreground">
                        {t("views.proposal.shoppingDeltaHint")}
                      </dd>
                    </div>
                  )}
                </dl>
              )}

              {/* Budget Model v2: goal impact is INFORMATION (soft goal);
                  a checkout conflict is a BLOCK (hard maximum, no consent). */}
              {proposal.impact.goalImpact && <GoalImpactLine goalImpact={proposal.impact.goalImpact} />}
              {proposal.impact.checkoutConflict === true && (
                <CheckoutConflictNotice {...conflictAmounts(proposal.impact)} />
              )}

              {phase === "ready" && proposal.validation.status === "FAIL" && (
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11"
                  onClick={requestProposal}
                >
                  {t("views.proposal.tryAnother")}
                </Button>
              )}
            </>
          )}
        </div>

        <SheetFooter>
          {proposal && proposal.validation.status === "PASS" && phase !== "checkoutConflict" && (
            <Button
              type="button"
              className="min-h-11"
              disabled={
                phase === "applying" ||
                !proposal.confirmationToken ||
                // Budget Model v2 (owner D-2): a checkout above the hard
                // maximum cannot be applied — no consent box can unlock it.
                proposal.impact.checkoutConflict === true
              }
              onClick={() => void handleApprove()}
            >
              {phase === "applying"
                ? t("views.proposal.approving")
                : intent === "lock"
                  ? t("views.proposal.lockApprove")
                  : intent === "unlock"
                    ? t("views.proposal.unlockApprove")
                    : t("views.proposal.approve")}
            </Button>
          )}
          {proposal && (proposal.validation.status === "PASS" || phase === "checkoutConflict") && (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={phase === "applying"}
              onClick={requestProposal}
            >
              {t("views.proposal.revise")}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            disabled={phase === "applying"}
            onClick={handleCancel}
          >
            {t("views.proposal.cancel")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

function mealDisplayTitle(meal: MealInstance | null, mealId: string): string {
  if (meal && meal.id === mealId) return meal.title?.en ?? humanizeRecipeId(meal.recipeId)
  return humanizeRecipeId(mealId)
}

function signed(n: number): string {
  return n > 0 ? `+${n}` : `${n}`
}

function formatSignedEur(eur: number, locale: "en" | "de"): string {
  const cents = Math.round(eur * 100)
  const formatted = formatEurFromCents(Math.abs(cents), locale)
  return cents > 0 ? `+${formatted}` : cents < 0 ? `-${formatted}` : formatted
}
